/**
 * /dashboard route guard — ensures appropriate access (Issue 15-frontend-gaps/04).
 *
 * Checks active workspace context + user role:
 * - Active context is 'personal' → allow access for all users (including system admins)
 * - Active context is 'company' → redirect to that company dashboard
 * - Active context is 'admin' → redirect to /admin/companies
 * - No context (fallback) → use whoami role-based logic
 *
 * Reading active-context first (from /api/me/workspaces) ensures the
 * workspace switcher is respected: an admin who explicitly chose the personal
 * workspace via ProfileMenu lands here, not in /admin/companies.
 */

import { type ReactElement, type ReactNode, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '@/lib/api-client';

interface DashboardGuardProps {
  children: ReactNode;
}

type GuardState = 'loading' | 'allowed' | 'redirecting';

interface WorkspacesResponse {
  ok: boolean;
  data?: {
    active_context: { kind: 'personal' | 'company' | 'admin'; company_id?: string };
    companies: Array<{ company_id: string }>;
  };
}

export function DashboardGuard({ children }: DashboardGuardProps): ReactElement {
  const navigate = useNavigate();
  const [state, setState] = useState<GuardState>('loading');

  useEffect(() => {
    void (async () => {
      // Use /api/me/workspaces to get the persisted active_context — this
      // respects the ProfileMenu workspace switcher (stored in users.active_workspace_id).
      let res: Response;
      try {
        res = await apiFetch('/api/me/workspaces', { credentials: 'same-origin' });
      } catch {
        navigate('/sign-in');
        return;
      }

      if (res.status === 401) {
        navigate('/sign-in');
        return;
      }

      const data = (await res.json()) as WorkspacesResponse;
      if (!data.ok || !data.data || !data.data.active_context) {
        navigate('/sign-in');
        return;
      }

      const ctx = data.data.active_context;

      if (ctx.kind === 'personal') {
        // Explicitly in personal workspace — allow Dashboard access for all users.
        setState('allowed');
        return;
      }

      if (ctx.kind === 'company' && ctx.company_id) {
        // Company workspace — redirect to that company's dashboard.
        setState('redirecting');
        navigate(`/company/${ctx.company_id}/dashboard`);
        return;
      }

      if (ctx.kind === 'admin') {
        // Admin workspace — redirect to admin area.
        setState('redirecting');
        navigate('/admin/companies');
        return;
      }

      // Fallback: no active context stored — infer from membership.
      // If they belong to exactly one company, redirect there. Otherwise allow.
      if (data.data.companies.length > 0) {
        const first = data.data.companies[0]!;
        setState('redirecting');
        navigate(`/company/${first.company_id}/dashboard`);
        return;
      }

      // Pure personal user — allow access.
      setState('allowed');
    })();
  }, [navigate]);

  if (state === 'loading') {
    return (
      <main className="flex items-center justify-center min-h-screen">
        <p className="text-muted">loading…</p>
      </main>
    );
  }

  if (state === 'redirecting') {
    return (
      <main className="flex items-center justify-center min-h-screen">
        <p className="text-muted">redirecting…</p>
      </main>
    );
  }

  return <>{children}</>;
}
